import { Button, cn } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  confirmDiscardDraft,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";
import { Rang1OnlyNotice } from "../components/rang1-only-notice";

type ConcernedDocument = {
  documentVersionId: number;
  documentId: number;
  label: string;
  index: string | null;
};

type Observation = {
  id: number;
  description: string;
  themeName: string | null;
  reportId: number;
  reportNumero: string | null;
  noticeId: number;
  nAvis: string | null;
  noticeStatusLabel: string | null;
  concernedDocuments: ConcernedDocument[];
  selectedDocumentVersionIds: number[];
  requiresResponse: boolean;
  responseDraft: string | null;
  treatedAt: string | null;
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: Observation[] };

type DraftState = {
  responseDraft: string;
  selectedIds: number[];
};

const draftOf = (obs: Observation): DraftState => ({
  responseDraft: obs.responseDraft ?? "",
  selectedIds: [...obs.selectedDocumentVersionIds],
});

function sameSelection(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

/**
 * Screen — "Préparer une réponse à observation" (écran 024).
 *
 * Lists the observations that "appellent une réponse" and have no response
 * yet, and lets the rang-1 travaux pilot edit the canonical response draft (`responseDraft`) and pick,
 * among the documents visés par l'avis, the subset the response addresses.
 * Draft edits are highlighted amber and staged until "Enregistrer"; leaving
 * the page with unsaved edits is guarded.
 *
 * Out of scope (handled elsewhere): marking whether an observation appelle une
 * réponse and its treatment, and generating the final response document.
 */
export function PreparerReponses() {
  const { yardId } = useParams<{ yardId: string }>();
  const { hasContractType } = useYardContext();
  const readOnly = !hasContractType("travaux_n_1");
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [drafts, setDrafts] = useState<Record<number, DraftState>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  // Keeps the other observations' unsaved edits; `resetId` takes the saved
  // values of the observation just saved.
  const fetchObservations = useCallback(
    async (resetId?: number) => {
      if (isNil(yardId)) return;
      try {
        const { data, error } = await apiClient.GET(
          "/api/yards/{yardId}/observations",
          { params: { path: { yardId } } },
        );
        if (error || isNil(data)) {
          setState({
            status: "error",
            error: "Erreur lors du chargement des observations",
          });
          return;
        }
        // Only observations still waiting for a response are prepared here.
        const items = data.data.items.filter(
          (obs) => obs.requiresResponse && isNil(obs.sentResponse),
        );
        setDrafts((prev) =>
          Object.fromEntries(
            items.map((obs) => [
              obs.id,
              obs.id === resetId || isNil(prev[obs.id])
                ? draftOf(obs)
                : prev[obs.id],
            ]),
          ),
        );
        setState({ status: "success", items });
      } catch {
        setState({
          status: "error",
          error: "Erreur lors du chargement des observations",
        });
      }
    },
    [yardId],
  );

  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  const items = state.status === "success" ? state.items : [];

  const isRowDirty = useCallback(
    (obs: Observation): boolean => {
      const draft = drafts[obs.id];
      if (isNil(draft)) return false;
      return (
        draft.responseDraft !== (obs.responseDraft ?? "") ||
        !sameSelection(draft.selectedIds, obs.selectedDocumentVersionIds)
      );
    },
    [drafts],
  );

  const isDirty = useMemo(
    () => items.some((obs) => isRowDirty(obs)),
    [items, isRowDirty],
  );

  useUnsavedChangesGuard(isDirty);

  const setResponseDraft = (id: number, responseDraft: string) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], responseDraft } }));
  };

  const toggleDocument = (id: number, documentVersionId: number) => {
    setDrafts((prev) => {
      const current = prev[id];
      if (isNil(current)) return prev;
      const selected = current.selectedIds.includes(documentVersionId)
        ? current.selectedIds.filter((d) => d !== documentVersionId)
        : [...current.selectedIds, documentVersionId];
      return { ...prev, [id]: { ...current, selectedIds: selected } };
    });
  };

  const saveResponse = async (obs: Observation) => {
    if (isNil(yardId)) return;
    const draft = drafts[obs.id];
    if (isNil(draft)) return;
    setSavingId(obs.id);
    try {
      const { error } = await apiClient.PATCH(
        "/api/yards/{yardId}/observations/{observationId}/response",
        {
          params: { path: { yardId, observationId: obs.id } },
          body: {
            responseDraft: draft.responseDraft.trim() || null,
            documentVersionIds: draft.selectedIds,
          },
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: error.error || "Échec de l'enregistrement",
        });
        return;
      }
      toaster.success({
        title: "Réponse préparée",
        description: "La réponse a été enregistrée",
      });
      await fetchObservations(obs.id);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement",
      });
    } finally {
      setSavingId(null);
    }
  };

  const cancelRow = (obs: Observation) =>
    confirmDiscardDraft(() =>
      setDrafts((prev) => ({ ...prev, [obs.id]: draftOf(obs) })),
    );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Préparer les réponses aux observations
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {items.length} observation{items.length > 1 ? "s" : ""} appelant une
            réponse
          </p>
        )}
        {readOnly && (
          <Rang1OnlyNotice>
            Seuls les pilotes de contrat travaux rang 1 préparent les réponses :
            vous les consultez sans pouvoir les modifier.
          </Rang1OnlyNotice>
        )}
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {state.status === "loading" ? (
            <p className="text-sm text-zinc-500">Chargement...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-zinc-500">
              Aucune observation n'appelle de réponse
            </p>
          ) : (
            <ul className="space-y-4">
              {items.map((obs) => {
                const draft = drafts[obs.id];
                const dirty = isRowDirty(obs);
                return (
                  <li
                    key={obs.id}
                    className={cn(
                      "rounded-lg border p-4",
                      dirty
                        ? "border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950"
                        : "border-zinc-200 dark:border-zinc-800",
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {obs.description}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">
                        Rapport {obs.reportNumero ?? "-"} · Avis{" "}
                        {obs.nAvis ?? "-"}
                        {obs.noticeStatusLabel
                          ? ` · ${obs.noticeStatusLabel}`
                          : ""}
                        {obs.themeName ? ` · ${obs.themeName}` : ""}
                      </p>
                    </div>

                    {obs.concernedDocuments.length > 0 && (
                      <fieldset className="mt-3">
                        <legend className="text-xs font-medium text-zinc-500">
                          Documents concernés par la réponse
                        </legend>
                        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                          {obs.concernedDocuments.map((doc) => (
                            <li key={doc.documentVersionId}>
                              <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                                <input
                                  type="checkbox"
                                  checked={
                                    draft?.selectedIds.includes(
                                      doc.documentVersionId,
                                    ) ?? false
                                  }
                                  disabled={readOnly}
                                  onChange={() =>
                                    toggleDocument(
                                      obs.id,
                                      doc.documentVersionId,
                                    )
                                  }
                                />
                                {doc.label}
                                {doc.index ? ` (${doc.index})` : ""}
                              </label>
                            </li>
                          ))}
                        </ul>
                      </fieldset>
                    )}

                    <div className="mt-3">
                      <label
                        htmlFor={`reponse-${obs.id}`}
                        className="text-xs font-medium text-zinc-500"
                      >
                        Réponse
                      </label>
                      <textarea
                        id={`reponse-${obs.id}`}
                        rows={3}
                        value={draft?.responseDraft ?? ""}
                        disabled={readOnly}
                        onChange={(e) =>
                          setResponseDraft(obs.id, e.target.value)
                        }
                        className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 disabled:cursor-not-allowed disabled:bg-zinc-100 disabled:text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:disabled:bg-zinc-800"
                      />
                    </div>

                    <div className="mt-3 flex justify-end gap-2">
                      <Button
                        type="button"
                        outline
                        onClick={() => cancelRow(obs)}
                        disabled={!dirty || savingId === obs.id}
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        onClick={() => saveResponse(obs)}
                        disabled={!dirty || savingId === obs.id}
                      >
                        {savingId === obs.id
                          ? "Enregistrement..."
                          : "Enregistrer"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
